import { useCallback, useEffect, useState } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../data/client';
import {
  useDeleteRecord,
  useFields,
  useRecord,
  useRestoreRecord,
  useUpdateRecord,
} from '../../data/hooks';
import {
  Button,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  ObjectTile,
  type SegmentDef,
  SegmentedControl,
  Skeleton,
  SplitDivider,
  useToast,
} from '../../ui';
import { ActivityFeed } from '../record/ActivityFeed';
import { NoteComposer } from '../record/NoteComposer';
import { highlightFieldIds, KeyFactBand } from '../record/RecordIdentity';
import { RecordOverview } from '../record/RecordOverview';
import { toggleKey, useLocalToggle } from '../record/useLocalToggle';
import { BarActions } from '../shell/WindowToolbar';
import { useOpenRecord } from './table/openRecord';
import { useRowSelection } from './table/RowSelectionProvider';
import { recordTitle } from './table/tableModel';
import { resolveBoardGroupField } from './views/viewState';

/**
 * THE DETAIL PANE (P4-1, P4-2; spec/screens/list.md section 2 items 5 and 9).
 *
 * It is where "3 leads selected" lives, and that placement is the whole point of the
 * decision: section 7's list says Apple does not float a bar over the content for actions
 * on a selection and does not show the count in the toolbar - Mail puts both in the pane
 * beside the list, and so does this.
 *
 * THE RECORD'S CONTENT IS THE RECORD SLICE'S OWN PARTS (round 3, R3-1). With one row
 * selected the pane draws what `spec/screens/list.md` section 2 item 9 lists, in that
 * order: a small toolbar row, `IdentityBand`, and then the record itself. NOTHING HERE IS A
 * SECOND COPY of a record part - every one is imported from `app/record/` with its own
 * props, so the pane and the page cannot drift apart in how a row looks, edits or reports a
 * refusal. The pane is a NARROWER PLACE to put them, and since the record's own wave-3
 * round 2 that is not even a stylesheet question: every part inside `RecordOverview` counts
 * the BOX it is drawn in, so the pane gets two columns from the same container query that
 * gives the record page three.
 *
 * AND IT IS THE RECORD'S OVERVIEW, UNDER THE RECORD'S OWN CONTROL (wave 3 round 2, brief
 * item 1; record round 2 item 4). The record became one content pane with Overview and
 * Activity, and this pane is that pane at another width: the same `ui/SegmentedControl` in
 * the same `content` variant chooses between `RecordOverview` and the record's stream. What
 * left with it is the last place this screen still assembled a record by hand - a
 * `PaneSection` named "Activity" holding `RecordTimeline` - which is exactly how the two
 * surfaces drifted before.
 *
 * THE PANE IS NOT SHOWN BELOW 1024px, and that is CSS rather than a condition here
 * (`.list-detail` in index.css, rulebook PH3): a row on a narrow window opens the record
 * page, which is what the title link has always done.
 */
export interface ListDetailPaneProps {
  readonly object: ObjectMetadata;
  /** The fields this VIEW shows: where the "Set ..." action's field comes from (F2). */
  readonly fields: readonly FieldMetadata[];
  /** The Archived segment: every selected row is a tombstone, so the exit is Restore. */
  readonly archived: boolean;
  /** The field the view groups its board by: what "stage" means on this object. */
  readonly boardGroupFieldId: string | null;
  /** The pane's width as CSS, dragged by the divider and remembered by this browser. */
  readonly width: string;
  /** The same width and its two limits as numbers: what the splitter announces and moves. */
  readonly widthPx: number;
  readonly minPx: number;
  readonly maxPx: number;
  readonly onWidth: (next: number) => void;
}

export function ListDetailPane({
  object,
  fields,
  archived,
  boardGroupFieldId,
  width,
  widthPx,
  minPx,
  maxPx,
  onWidth,
}: ListDetailPaneProps) {
  const { selection } = useRowSelection();
  const count = selection.ids.length;

  return (
    <aside className="list-detail" style={{ width }} aria-label={`${object.labelSingular} detail`}>
      {/*
        ONE DIVIDER CONSTRUCTION IN THE PRODUCT (wave 3 round 2, brief item 4). This pane
        wrote its own `role="separator"` in P4-2 and the shell later needed the same thing for
        the sidebar, so `ui/SplitDivider` is the primitive both take: the same splitter role,
        the same 16px arrow step and 64px Shift step, the same `min`, `ideal` and `max`
        contract Apple's own column modifier has
        (doc: https://developer.apple.com/documentation/swiftui/view/navigationsplitviewcolumnwidth(min:ideal:max:),
        hig: https://developer.apple.com/design/human-interface-guidelines/split-views).
        `side="trailing"` is the whole difference between the two dividers in this window: a
        drag to the left widens a trailing pane and narrows a leading one. The numbers are the
        pane's own and keep their provenance - the floor is `--list-detail-min`, the ideal is
        `--list-detail-width` and the ceiling is half the SPLIT, which is a proportion rather
        than a measure and therefore needs no token (`useDetailMaxWidth`).
      */}
      <SplitDivider
        label="Resize the detail pane"
        width={widthPx}
        min={minPx}
        max={maxPx}
        side="trailing"
        onWidth={onWidth}
        className="list-detail-grip"
      />
      {/*
        THE PANE SCROLLS INSIDE, THE DIVIDER DOES NOT (BR-RECORD, register A6-14: "Man muss halt
        genau auf die Kante klicken"). The divider is absolutely placed in the pane, and while the
        pane itself was the scroller the divider scrolled with its content: 600px down a record,
        it covered the top 208px of an 808px edge and the rest of the edge was not a divider at
        all (measured 4 Oct on the fixture build). The content scrolls in this wrapper, so the
        grab strip stands the full height of the edge whatever the record's length.
      */}
      <div className="list-detail-scroll">
        {count === 0 ? (
          /**
           * A PANE WITH NOTHING SELECTED SAYS THAT, AND NOTHING ELSE (wave 4, C2-9; owner-75
           * "Das rechte Panel ist irgendwie immer sichtbar ... sollte sich nur oeffnen, wenn
           * man auf ein Record draufklickt").
           *
           * MAIL'S MODEL, WHICH IS THE ONE THE PANE ALREADY HAS: the detail column stays
           * visible without a selection and carries one sentence, centred, in the secondary
           * label colour - "No Message Selected" - with no icon, no button and no description
           * of the mailbox. Finder's model (no preview until View > Show Preview) is Apple's
           * other answer and the owner may still ask for it; what this round removes is the
           * third thing, which is neither.
           * hig: https://developer.apple.com/design/human-interface-guidelines/split-views
           *
           * IT IS GENERATED FROM THE OBJECT'S OWN SINGULAR LABEL, which is what round 3's R3-2
           * argued against and had backwards: it put the object's DESCRIPTION here - a
           * definition of the object rather than a statement about this pane - and an object
           * whose admin never wrote one got a blank column with no explanation at all. The
           * sentence is now the same for every object and every object has one. The
           * description keeps its two homes, the Objects and fields admin where it is written
           * and the list's own empty state, and moves nowhere.
           */
          <p className="flex flex-1 items-center justify-center px-4 py-12 text-center text-sm text-ink-muted">
            {/* SENTENCE CASE (decision page item 2.1 answered A; DESIGN.md D7): the empty-state
              noun title of Mail's construction (COPY-DECK-2026-09-12.md section 4 R4), "No
              Agency selected". The object's own singular label supplies the noun as configured,
              as every label built from it does ("Move Agencies up", "Add Deal…"). */}
            {`No ${object.labelSingular} selected`}
          </p>
        ) : count === 1 ? (
          /**
           * KEYED ON THE RECORD, so moving the selection to another row resets the pane's
           * own state rather than carrying it across: an open note panel belongs to the
           * record it was opened on, and React would otherwise keep the mounted component
           * and hand it a different id.
           */
          <SingleRecord
            key={selection.ids[0] as string}
            object={object}
            recordId={selection.ids[0] as string}
          />
        ) : (
          <ManyRecords
            object={object}
            fields={fields}
            recordIds={selection.ids}
            anchor={selection.anchor}
            archived={archived}
            boardGroupFieldId={boardGroupFieldId}
          />
        )}
      </div>
    </aside>
  );
}

/**
 * THE TWO HALVES OF A RECORD, AS THE RECORD PAGE NAMES THEM (record wave 3 round 2, item
 * 5a; `spec/screens/record.md` section 2 item 5a). The kit's content-area DUO, which is
 * `ui/SegmentedControl`'s `content` variant - the same master and the same variant the
 * record page's own control takes, because this is that control on another surface and not
 * a second one.
 * kit: Apple macOS 27 UI Kit.sketch / Segmented Controls/Light/Content Area/Duo/Active, 3 Rg
 */
type ContentSegment = 'overview' | 'activity';

const CONTENT_SEGMENTS: readonly SegmentDef<ContentSegment>[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'activity', label: 'Activity' },
];

/**
 * ONE ROW SELECTED: the record, out of the record slice's own parts (round 3, R3-1; wave 3
 * round 2, brief item 1; spec/screens/list.md section 2 item 9).
 *
 * THE ORDER IS THE SPEC'S READING ORDER - the toolbar row, the identity band condensed to
 * one row, the content control, and the half it chooses - and each part is the record
 * page's component with its props. What the pane decides is which parts and how wide;
 * what a part LOOKS like is the record slice's, and the two surfaces stay identical
 * because there is only one of each.
 *
 * IT COSTS NO REQUEST THE PAGE HAS NOT MADE. The record itself is the canonical cache
 * entry the table already seeded (INV-13) and the fields are the metadata query the list
 * page holds open, both under keys TanStack shares; the timeline and the health config
 * are the two the record's own parts ask for, cached per record and per session.
 */
function SingleRecord({
  object,
  recordId,
}: {
  readonly object: ObjectMetadata;
  readonly recordId: string;
}) {
  const record = useRecord(object.id, recordId);
  const fields = useFields(object.id);
  const open = useOpenRecord(object.id);
  /**
   * WHICH HALF THIS PERSON READS, REMEMBERED THE WAY THE RECORD REMEMBERS IT (brief item 1):
   * the record page's own `useLocalToggle` under the record page's own key, per person and
   * per object, Overview the default. THE SAME KEY DELIBERATELY, not a second one beside it:
   * a reader who is in a record's Activity and presses Open has not changed his mind about
   * which half of the record he is reading, and a pane that agreed with the page on
   * everything except this would be the drift this round exists to remove.
   */
  const [onActivity, setOnActivity] = useLocalToggle(toggleKey('activity', object.id), false);
  /**
   * S1-4's behaviour, as the record page wires it: every request returns to the composer
   * and an already-open draft survives it, so pressing New Note… twice refocuses rather
   * than remounting. The panel is the record slice's own floating, non-modal one.
   */
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteRequest, setNoteRequest] = useState(0);

  const row = record.data;
  const title =
    row === undefined || row === null ? '' : recordTitle(row, object.labelIdentifierFieldId);
  /** D21: a tombstone resolves and renders read-only, so there is nothing to add to it. */
  const deleted = row !== undefined && row !== null && row.deletedAt !== null;
  /** One opener for the two controls that ask for a note, as the record page has one. */
  const openNote = () => {
    setNoteOpen(true);
    setNoteRequest((request) => request + 1);
  };

  return (
    <div className="list-detail-record">
      {/* THE PANE'S FIRST LINE IS ITS TOOLBAR ROW (spec section 2 item 9, round 2 finding
          F3): "Open", which promotes the record to its own page, and the record's
          prominent action beside it - on the same top edge as the list's header row, so
          the two panes' first lines sit on one line. `.list-detail-first` carries those
          metrics and `Button variant="push"` is the kit's content-area button the header
          row and the footer take too, so the screen has one button family rather than three.

          IT IS A BUTTON AND NO LONGER A LINK (wave 3, L-B2). As a `TransitionLink` with the
          capsule class it was an anchor drawn as an outlined web capsule - the shape the
          owner named - and it cost what a link on a row cost: Cmd-clicking it was Chrome's
          new tab rather than the product's own "Open in New Tab", which the row menu offers
          by name. One open path for the whole screen now (`useOpenRecord`). */}
      {/* AND THE ROW IS A BAR, SO ITS ITEMS ARE THE BAR'S (wave 5 round 5, C6-6). `BarActions`
          is the window toolbar's own symbol conversion with the frame's measurement left out,
          so "New Note…" is the same pencil symbol here as in the record's bar, and "Open"
          keeps its word because the hairline set holds no mark for it. */}
      <BarActions
        className="list-detail-first list-detail-actions"
        actions={
          <>
            <Button variant="push" size="sm" onClick={() => open(recordId)}>
              Open
            </Button>
            {!deleted && (
              <Button variant="primary" icon="pencil" onClick={openNote}>
                New note…
              </Button>
            )}
          </>
        }
      />

      {row === undefined || row === null || fields.data === undefined ? (
        /* The same geometry the loaded pane has - a band line and a group box - because a
           pane that changes shape while it resolves reads as a layout bug rather than as
           a wait. The record is usually already in the cache, so this is rarely seen. */
        <div className="flex flex-col gap-4 py-2" aria-busy="true">
          <Skeleton
            className="h-6 w-48"
            label={`Loading the ${object.labelSingular.toLowerCase()}`}
          />
          <Skeleton shape="block" className="h-40" />
        </div>
      ) : (
        <>
          {/* THE RECORD PAGE'S OWN BAND, values as controls (BR-RECORD): the rows below leave
              the key facts out, so the band is where one is corrected. */}
          <KeyFactBand
            object={object}
            fields={fields.data}
            record={row}
            name={title}
            tile={<ObjectTile icon={object.icon ?? null} color={object.color ?? null} size="lg" />}
          />
          {/* THE CONTROL SITS UNDER THE BAND, which is where the record page puts it and
              where Apple puts a control that scopes the content below it. Both classes are
              the record's own: the pane is the record at another width, so it takes the
              record's metrics rather than declaring a second set. */}
          <div className="record-segments">
            <SegmentedControl
              value={onActivity ? 'activity' : 'overview'}
              onValueChange={(value) => setOnActivity(value === 'activity')}
              segments={CONTENT_SEGMENTS}
              ariaLabel="Record sections"
              variant="content"
            />
          </div>
          <div className="record-content">
            {onActivity ? (
              /* THE STREAM AT THE PANE'S WIDTH, and it is the page's own Activity half:
                 the chip row, the entry row that opens the writing panel, and the rows.
                 Round 3 left the chip row out and named a 480px pane as the reason; the
                 pane is the record's second surface since the record's own round 2, and a
                 filter that exists on one of them and not the other is the drift. */
              <ActivityFeed
                objectId={object.id}
                recordId={recordId}
                fields={fields.data}
                {...(deleted ? {} : { onAddNote: openNote })}
              />
            ) : (
              /* THE SPEC'S READING ORDER, WRITTEN DOWN ONCE (record round 2): the field
                 sections as group boxes that edit in place, identity and merges, the tasks,
                 what points at this record, and the created-and-updated line. Every write
                 goes through the record's own save hook, which is why the row behind the
                 pane updates without a reload (`useUpdateRecord` writes the canonical entry
                 and reconciles the filtered lists, INV-6 and INV-13). */
              <RecordOverview
                object={object}
                fields={fields.data}
                record={row}
                hiddenFieldIds={highlightFieldIds(fields.data, row)}
              />
            )}
          </div>
        </>
      )}

      <NoteComposer
        objectId={object.id}
        recordId={recordId}
        open={noteOpen && !deleted}
        focusRequest={noteRequest}
        onOpenChange={setNoteOpen}
      />
    </div>
  );
}

/**
 * SEVERAL ROWS SELECTED: the count, then the actions as the kit's CONTENT-AREA BORDERED
 * BUTTONS - which is what they say they are since wave 3 and were not before: the variant
 * was `secondary`, and every `secondary` is `.glass-control`, so the pane's actions were
 * drawn in the navigation layer's material on a content ground. `push` is the master this
 * comment always named (kit: Content Area/Light|Dark/01 - Bordered/01 - Idle). Mail's
 * construction, and the one the mockup review accepted.
 *
 * OPEN IS HERE TOO, AND IT OPENS THE ANCHOR (wave 3, L-B3: "Open stays available on a
 * multi-row selection"). It used to disappear the moment a second row joined the selection,
 * which is the one moment a reader is most likely to want the row they started from: the
 * anchor is exactly that row - where the last plain click or Cmd-click landed
 * (`rowSelection.ts`) - so "Open" needs no second meaning to stay available.
 *
 * TWO OF THE SPEC'S FOUR ACTIONS ARE HERE AND TWO ARE NOT, and the two that are missing are
 * missing for the reason the rulebook gives rather than for lack of room: "Assign to" needs
 * an owner concept the platform does not have, and "Add task" needs the task composer's own
 * parent, which is the record slice's surface. S9 says a menu HIDES what does not apply, so
 * they are absent rather than dimmed, and the report names them as the round-2 gap.
 */
function ManyRecords({
  object,
  fields,
  recordIds,
  anchor,
  archived,
  boardGroupFieldId,
}: {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly recordIds: readonly string[];
  /** Where the selection started: the row "Open" means while several are selected. */
  readonly anchor: string | null;
  readonly archived: boolean;
  readonly boardGroupFieldId: string | null;
}) {
  const toast = useToast();
  const open = useOpenRecord(object.id);
  const update = useUpdateRecord(object.id);
  const remove = useDeleteRecord(object.id);
  const restore = useRestoreRecord(object.id);

  const stageFieldId = resolveBoardGroupField(boardGroupFieldId, fields);
  const stage = fields.find((field) => field.id === stageFieldId);
  const stageOptions = stage?.options?.selectOptions ?? [];
  const subject = `${recordIds.length} ${object.labelPlural.toLowerCase()}`;

  return (
    <div className="flex min-w-0 flex-col gap-4 pb-4">
      {/* THE COUNT IS THE PANE'S HEADING, in the pane's own register: a sentence, not a
          badge, because it is what this pane is ABOUT - and it is the pane's FIRST LINE, on
          the top edge the list's header row starts at (round 2, finding F3). */}
      {/* A TITLE STATING THE COUNT (copy deck section 3, the ListDetailPane row; R6 numerals
          and a real plural; sentence case per decision page item 2.1 and DESIGN.md D7): "3
          Agencies selected", the object's label as configured. The lower-case `subject` below
          is the same fact inside a SENTENCE, which is where it belongs lower-cased. */}
      <h2 className="list-detail-first truncate text-base font-semibold text-ink">{`${recordIds.length} ${object.labelPlural} selected`}</h2>
      {/* THE SELECTION'S ACTIONS ARE THE SAME BAR (wave 5 round 5, C6-6), and every one of
          them keeps its word: "Open", "Set <stage>", "Restore" and "Archive" carry no symbol,
          which is the HIG sentence's own exception rather than an omission. Routing the row
          through `BarActions` is what makes that a decision the code states instead of a
          place the rule never reached. */}
      <BarActions
        className="flex flex-wrap gap-2"
        actions={
          <>
            {anchor !== null && (
              <Button variant="push" size="sm" onClick={() => open(anchor)}>
                Open
              </Button>
            )}
            {!archived && stage !== undefined && stageOptions.length > 0 && (
              <DropdownMenu
                align="start"
                trigger={<Button variant="push" size="sm">{`Set ${stage.label}`}</Button>}
              >
                <DropdownLabel>{stage.label}</DropdownLabel>
                {stageOptions.map((option) => (
                  <DropdownItem
                    key={option.id}
                    onSelect={() => {
                      for (const recordId of recordIds) {
                        update.mutate({ recordId, values: { [stage.id]: option.id } });
                      }
                      toast({ title: `${subject} moved to ${option.label}`, tone: 'neutral' });
                    }}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
            {archived ? (
              <Button
                variant="push"
                size="sm"
                onClick={() => {
                  for (const recordId of recordIds) restore.mutate(recordId);
                  toast({ title: `${subject} restored`, tone: 'success' });
                }}
              >
                Restore
              </Button>
            ) : (
              <Button
                variant="push"
                size="sm"
                onClick={() => {
                  for (const recordId of recordIds) remove.mutate(recordId);
                  toast({ title: `${subject} archived`, tone: 'neutral' });
                }}
              >
                Archive
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}

/**
 * THE DIVIDER'S THREE NUMBERS - minimum, ideal and maximum - which is the contract Apple's
 * own column modifier has. The drag and the arrow keys cannot disagree about them any more,
 * because since wave 3 round 2 neither of them is written here: `ui/SplitDivider` clamps what
 * it moves, and these functions only say what the limits ARE.
 *
 * THE FLOOR IS THE TOKEN, READ OFF THE DOCUMENT rather than written here as a number: the
 * pane's minimum is `--list-detail-min` and DESIGN.md's "tokens only" means a component may
 * not carry a second copy of it that can drift. The ceiling is half the SPLIT - the point past
 * which the list stops being the thing this screen is for - and it needs no token because it is
 * a proportion rather than a measure; `.list-detail`'s own `max-width: 50%` is the same
 * proportion said in CSS, and the two have to agree (`useDetailMaxWidth`).
 */
function tokenPx(name: string): number {
  const root = document.documentElement;
  const style = getComputedStyle(root);
  const raw = style.getPropertyValue(name).trim();
  const value = Number.parseFloat(raw);
  if (Number.isNaN(value)) return 0;
  return raw.endsWith('rem') ? value * Number.parseFloat(style.fontSize) : value;
}

export function detailMinWidth(): number {
  return tokenPx('--list-detail-min');
}

/** What the pane is wide by default, so the splitter can announce a number it really is. */
export function detailDefaultWidth(): number {
  return tokenPx('--list-detail-width');
}

export function detailMaxWidth(splitWidth: number, min: number): number {
  return Math.max(min, Math.round(splitWidth / 2));
}

export function clampDetailWidth(candidate: number, splitWidth: number, min: number): number {
  return Math.min(Math.max(Math.round(candidate), min), detailMaxWidth(splitWidth, min));
}

/**
 * THE CEILING IS HALF THE SPLIT, AND IT IS MEASURED OFF THE SPLIT (wave 3 round 2, found by
 * driving the divider rather than by reading it).
 *
 * `SplitDivider` announces `aria-valuemax`, which is the first time this pane has had to state
 * its ceiling as a number - and the number the arithmetic had, half the WINDOW, is not the one
 * the pane obeys: `.list-detail`'s `max-width: 50%` is half its CONTAINER, so at 1440 the drag
 * clamped at 720 while the pane painted 546 and the splitter announced a width it could not
 * reach. Half the split is what both halves mean, so it is what both halves now read, from the
 * box itself through a `ResizeObserver` - which also catches the one resize a window listener
 * cannot see: the SIDEBAR being dragged, which changes the content's width and nothing else.
 */
export function useDetailMaxWidth(split: HTMLElement | null, min: number): number {
  const [width, setWidth] = useState(0);
  /**
   * THE ELEMENT ITSELF AND NOT A REF, which is the second half of the same finding: the split
   * is rendered after the object's metadata resolves, so a `useRef` was still null when this
   * effect ran and the ref object never changes - so it never ran again, the width stayed 0,
   * and the first drag clamped the pane to its floor. A callback ref puts the element in state,
   * which is a dependency React can see.
   */
  useEffect(() => {
    if (split === null) return;
    const read = () => setWidth(split.getBoundingClientRect().width);
    read();
    const observer = new ResizeObserver(read);
    observer.observe(split);
    return () => observer.disconnect();
  }, [split]);
  return detailMaxWidth(width, min);
}

/**
 * THE PANE'S WIDTH, REMEMBERED BY THIS BROWSER.
 *
 * The spec says the two panes remember their width PER PERSON. This is the browser's half of
 * that and is deliberately named as such in the ledger: a width is a preference about
 * LOOKING, which is the same class of thing `table-completion.md` D4 keeps in local storage
 * for rows per page, and the per-user store is a network round trip on every drag. Moving it
 * to the preference store is a later slice's one-line change - the read and the write are
 * both here.
 *
 * A STORE THAT REFUSES IS NOT AN ERROR. Private windows and locked-down profiles throw on
 * `localStorage`, and a pane that cannot remember its width still has one.
 */
const DETAIL_WIDTH_KEY = 'ofmos.list.detailWidth';

export function useRememberedDetailWidth(): readonly [number | null, (next: number) => void] {
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const stored = window.localStorage.getItem(DETAIL_WIDTH_KEY);
      const parsed = stored === null ? Number.NaN : Number.parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  const remember = useCallback((next: number) => {
    setWidth(next);
    try {
      window.localStorage.setItem(DETAIL_WIDTH_KEY, String(next));
    } catch {
      // The width still works for this session; nothing to tell the reader.
    }
  }, []);

  return [width, remember];
}
